/*global SVP*/
'use strict';
import Sizzle from 'sizzle';

import Config from '../config';
import resolveStreamingConfig from './resolve-streaming-config';

import metadataFetchFromSource from '../api/metadata-fetch-from-source';

import device from '../util/device';
import * as domUtils from '../util/dom';
import SVPCustomEvent from '../util/custom-event';
import debugUtil from '../util/debug';
import isImmersiveStream from '../util/is-immersive-stream';
import VideoType from '../constants/video-type';
import ErrorType from '../constants/error-type';
import { isExpectedError } from '../util/error';

import dash from './dash/dash-video';
import hls from './hls/hls-video';
import flashVideo from './flash-video';
import * as chromecast from './chromecast-video';
import { isAutoplayOfNextVideo } from '../util/video-attributes';

const Debug = debugUtil('wrapperApi');

const NAMESPACE = 'wrapper';
const READY_NAMESPACE = 'wrapper-ready';

const STATE_CLASS = {
	forceHide: 'svp_is--force-hide'
};

function applyFunctionToFilters(filters, fnName, args) {
	if (!filters) {
		return true;
	}

    for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        if (hasFunction(filter, fnName)) {
            let retValue = filter[fnName].apply(filter, args);

            if (isValidReturnValue(retValue, fnName)) {
                return retValue;
            }
        }
    }

	return null;
}

function hasFunction(filter, fnName) {
    return filter && typeof filter[fnName] === 'function';
}

function isValidReturnValue(retValue, fnName) {
    return (fnName.match(/^is/) || retValue !== false) && retValue !== null && retValue !== undefined;
}

function hasSourceElementChild(element) {
    return Sizzle('source', element).length > 0;
}

function getEvents(videoObject) {
	// TODO: Which of these do we need/want to support?
	let events = [
		'abort',
        'bufferEmpty',
		'canplay',
		'canplaythrough',
		'durationchange',
		'emptied',
		'ended',
		'error',
		'loadeddata',
		'loadedmetadata',
		'loadstart',
		'pause',
		'play',
		'playing',
		'progress',
		'ratechange',
		'resize',
		'seeked',
		'seeking',
		'stalled',
		'suspend',
		'timeupdate',
		'volumechange',
		'waiting'
	];

	if (videoObject.customEvents) {
		events = events.concat(videoObject.customEvents);
	}

	return events;
}

function cleanEvents(videoObject) {
	if (videoObject.removeEventSVP) {
		getEvents(videoObject).forEach((eventName) => {
			videoObject.removeEventSVP(NAMESPACE + '.' + eventName);
		});
	}
}

function attachEvents(rootElement, videoObject) {
	if (videoObject.addEventSVP) {
		getEvents(videoObject).forEach((eventName) => {
			videoObject.addEventSVP(NAMESPACE + '.' + eventName, (event) => {
				if (event.preventDefault) {
					event.preventDefault();
				}

				if (eventName !== 'timeupdate') {
				    Debug('received event: ' + eventName);
                }

				rootElement.dispatchEvent(new SVPCustomEvent(event.type, { detail: event }));

				return false;
			});
		});
	}
}

function handleError(wrapperApi) {
	wrapperApi.on(ErrorType.UNKNOW_VIDEO_TYPE_ERROR, function (e) {
		// If no plugin has handled this, send an error
		if (!e.defaultPrevented) {
			wrapperApi.fire('error', {
				name: ErrorType.UNKNOW_VIDEO_TYPE_ERROR
			});
		}

		wrapperApi.off(ErrorType.UNKNOW_VIDEO_TYPE_ERROR);
	});

	wrapperApi.fire(ErrorType.UNKNOW_VIDEO_TYPE_ERROR, null, {
        cancelable: true
    });
}

function _videoObjectReadyFn(wrapperApi, videoObject, isExternalUi, options, cancelToken) {
    if (cancelToken.cancelled) {
        if (cancelToken.unload) {
            cancelToken.unload();
        }
        Debug.log('Cancel in _videoObjectReadyFn');
        if (cancelToken.onCancel) {
            cancelToken.onCancel();
            cancelToken.onCancel = null;
        }

        return;
    }

    Debug.log('_videoObjectReadyFn', options);
	if (!wrapperApi.state()) {
		return;
	}
	wrapperApi.state().loading = false;
	wrapperApi.state().ready = true;
	wrapperApi._data._video = videoObject;

	domUtils.removeEvents(videoObject, 'loadstart', READY_NAMESPACE);

	wrapperApi.fire('ready', {
		videoId: wrapperApi.data() && wrapperApi.data().videoId,
		externalUi: isExternalUi
	});

    _setupResetOnEndListener(wrapperApi, isExternalUi);
}

function _setupResetOnEndListener(wrapperApi, isExternalUi) {
    if (!Config.resetOnEnd) {
        return;
    }

    wrapperApi.once('reset.ended', () => {
        const videoId = wrapperApi.data() && wrapperApi.data().videoId;

        if (!videoId) {
            return;
        }

        wrapperApi.once('reset.seeked', () => wrapperApi.pause());

        setTimeout(() => {
            wrapperApi.reset();
            wrapperApi.fire('ready', {
                videoId: videoId,
                externalUi: isExternalUi
            });

            _setupResetOnEndListener(wrapperApi, isExternalUi);
        }, 100);
    });
}

function _clearResetOnEndListener(wrapperApi) {
    wrapperApi.off('reset.ended');
    wrapperApi.off('reset.seeked');
}

function _setup(wrapperApi, videoId, metadata, videoUrls, textUrls, options, cancelToken) {
    Debug.log('_setup', videoId, options);

    const oldType = wrapperApi.data().videoConfig.type;

    if (cancelToken.cancelled) {
        Debug.log('Cancel in _setup');
        if (cancelToken.onCancel) {
            cancelToken.onCancel();
            cancelToken.onCancel = null;
        }

        return;
    }

	if (!wrapperApi.state()) {
        Debug.log('No state');

		return;
	}

    _clearResetOnEndListener(wrapperApi);

    let videoObject;
    let videoElement = Sizzle('video', wrapperApi.element())[0];
    // TODO: We do not have plugins loaded here, we can thus not check if immersive plugin will be loaded
    const streamingConfig = resolveStreamingConfig(videoUrls, textUrls, metadata, videoElement);

	wrapperApi._data.videoId = videoId;
    wrapperApi._data.usingChromecastModule = false;
    wrapperApi.setStreams(streamingConfig);

    const currentStream = options.forceVideoType ?
        streamingConfig.find(
            (format) => format.type === options.forceVideoType
        ) :
        streamingConfig[0];

    wrapperApi._data.videoConfig = currentStream;

    /*
     * Force disable timeshift for all immersive (360) streams. This is
     * required because it is not possible to disable DVR on specific
     * video urls in Escenic. For now, all 360-streams will be without
     * DVR.
     */
    if (isImmersiveStream(currentStream)) {
        metadata.timeshiftEnabled = false;
    }

	wrapperApi.fire('metadata', metadata);


	if (window.SVP._inSweden !== undefined) {
		if (!window.SVP._inSweden && (metadata.geoBlockedSweden || (metadata.rights && metadata.rights.geoBlockedSweden))) {
			//TODO rewrite this once we decide how to handle rights in the api
			wrapperApi.fire('error', { name: ErrorType.GEO_BLOCK_ERROR });

			return;
		}
	}

    const useChromecastModule = Boolean(options.chromecast);

    Debug.log('new type', oldType, currentStream.type, useChromecastModule, wrapperApi._data.usingChromecastModule);
	const isNewType = (
        oldType !== currentStream.type ||
        useChromecastModule !== wrapperApi._data.usingChromecastModule
    );
    // video().unload = null ?
	if (wrapperApi.video().unload && isNewType) {
		Debug('Switching video format: ' + oldType + ' --> ' + currentStream.type);
		cleanEvents(wrapperApi.video());
		wrapperApi.video().unload();
		videoElement = Sizzle('video', wrapperApi.element())[0];
	}

    const autoplay = wrapperApi.isAutoplay();
    if (autoplay) {
        videoElement.setAttribute('autoplay', '');
        videoElement.autoplay = true;
    } else {
        videoElement.removeAttribute('autoplay');
        videoElement.autoplay = false;
    }

    if (useChromecastModule) {
        wrapperApi._data.usingChromecastModule = true;
        videoObject = chromecast.load(videoId, videoElement, wrapperApi, options);
        cancelToken.unload = videoObject.unload;

        domUtils.addEvents(videoObject, 'loadstart', () => {
            _videoObjectReadyFn(wrapperApi, videoObject, false, options, cancelToken);
        }, READY_NAMESPACE);

        if (isNewType) {
            attachEvents(wrapperApi.element(), videoObject);
        }

        return;
    }

	switch (currentStream.type) {
		case VideoType.DASH:
        case VideoType.IMMERSIVE_DASH_STEREO_TB:
        case VideoType.IMMERSIVE_DASH_STEREO_LR:
        case VideoType.IMMERSIVE_DASH_MONO: {
            require.ensure([], (require) => {
                if (cancelToken.cancelled) {
                    Debug.log('Cancel in _setup:dash');
                    if (cancelToken.onCancel) {
                        cancelToken.onCancel();
                        cancelToken.onCancel = null;
                    }

                    return;
                }

                const dashjs = require('dashjs')['default'];
                videoObject = dash.load(videoId, videoElement, currentStream.videoUrl, metadata, wrapperApi.config, wrapperApi, dashjs, options);
                cancelToken.unload = videoObject.unload;

                domUtils.addEvents(videoObject, 'loadstart', () => {
                    _videoObjectReadyFn(wrapperApi, videoObject, false, options, cancelToken);
                }, READY_NAMESPACE);

                if (isNewType) {
                    attachEvents(wrapperApi.element(), videoObject);
                }
            }, 'dash');
            break;
        }
        case VideoType.HLS:
        case VideoType.IMMERSIVE_HLS_STEREO_TB:
        case VideoType.IMMERSIVE_HLS_STEREO_LR:
        case VideoType.IMMERSIVE_HLS_MONO: {
            videoObject = hls.load(videoElement, currentStream.videoUrl, metadata, wrapperApi.config, wrapperApi, options);

            domUtils.addEvents(videoObject, 'loadstart', () => {
                _videoObjectReadyFn(wrapperApi, videoObject, false, options, cancelToken);
            }, READY_NAMESPACE);

            if (isNewType) {
                // On Android 4.4.2 and below, the poster image sometimes isn't removed properly. Seeking fixes this.
                if (device.isBrokenAndroidChrome()) {
                    domUtils.addEvents(wrapperApi.element(), 'timeupdate', () => {
                        if (wrapperApi.getCurrentTime() > 0) {
                            domUtils.removeEvents(wrapperApi.element(), 'timeupdate', 'androidWrapper');
                            wrapperApi.seek(Math.floor(wrapperApi.getCurrentTime()));
                        }
                    }, 'androidWrapper');
                }
                attachEvents(wrapperApi.element(), videoObject);
            }
			break;
        }
		case VideoType.HDS:
		case VideoType.FLASHLS: {
		    const oldVideoObject = wrapperApi._data._video;
            const flashEngine = Config.flashEngine || flashVideo;
			videoObject = flashEngine.load(videoElement, currentStream.videoUrl, currentStream.textUrl, metadata, autoplay, options);
            cancelToken.unload = videoObject.unload;

			if (oldVideoObject) {
                cleanEvents(oldVideoObject); //clear events on old obj
            }
            attachEvents(wrapperApi.element(), videoObject);

			_videoObjectReadyFn(wrapperApi, videoObject, false, options, cancelToken);
            if (wrapperApi.isAutoplay()) {
                wrapperApi.play();
            }
			break;
        }
		default: {
			handleError(wrapperApi);
			_videoObjectReadyFn(wrapperApi, videoElement, true, options, cancelToken);
			if (isNewType) {
				attachEvents(wrapperApi.element(), videoElement);
			}
			break;
        }
	}

}


var Props = function (videoId, rootElement) {
	this.videoId = videoId;
	this.allowNoUi = true;
	this.isRemote = false;
	this._video = {};
	this._root = rootElement;
    this.usingChromecastModule = false;

	this.videoConfig = {
		videoType: VideoType.UNKNOWN
	};
};

Props.prototype.destroy = function () {
	//Set all references to undefined
	var key;
	for (key in this) {
		if (Object.prototype.hasOwnProperty.call(this, key)) {
			this[key] = undefined;
		}
	}
};

var State = function () {
	this.ready = false;
	this.loading = false;
	this.hidden = false;
};

export default class WrapperAPI {
	constructor(videoId, rootElement, config) {
		this._data = new Props(videoId, rootElement);
		this._state = new State();
        this._loadCancelToken = null;
		this.filters = [];
		this.device = device;
		this.config = config;
		this.fullscreenElement = null;
		this.autoplay = this.config.autoplay;
		this.splash = Config.splash;
		this.streams = null;
		this.autoplayOfNextVideo = false;
	}

	data() {
		return this._data;
	}

	state() {
		return this._state;
	}

	element() {
		return this._data && this._data._root;
	}

	video() {
		return this._data && this._data._video;
	}

	isAutoplay() {
	    return this.autoplay;
    }

    hasSplash() {
	    return this.splash;
    }

    load(videoId, autoplay, options) {
        // Cancel other loading tasks
        if (this._state.loading && this._loadCancelToken) {
            Debug.log('Cancelling');
            this._loadCancelToken.cancelled = true;
            this._loadCancelToken.onCancel = () => {
                Debug.log('onCancel()', videoId, autoplay, options);
                this._state.loading = false;
                this.load(videoId, autoplay, options);
            };

            return;
        }

        const cancelToken = {
            cancelled: false,
            videoObject: null
        };

        this._loadCancelToken = cancelToken;

        if (videoId) {
            this._data.videoId = videoId;
        }

        if (!options) {
            options = {};
        }

        Debug('load(videoId, autoplay)', this._data.videoId, autoplay, options);

        this._state.ready = false;
        this._state.loading = true;

        this.autoplay = autoplay !== undefined ? autoplay : this.config.autoplay;
        this.splash = options.splash !== undefined ? options.splash : Config.splash;

        this.autoplayOfNextVideo = Boolean(
            options.autoplayOfNextVideo !== undefined ?
                options.autoplayOfNextVideo : isAutoplayOfNextVideo(Sizzle('video', this.element())[0])
        );

		if (applyFunctionToFilters(this.filters, 'load', [videoId, autoplay, options])) { // eslint-disable-line prefer-rest-params
            if (cancelToken.onCancel) {
                cancelToken.onCancel();
            }

			return;
		}

        const callback = (metadata, videoUrls, textUrls) => {
            _setup(this, this._data.videoId, metadata, videoUrls, textUrls, options, cancelToken);
        };
		const errorHandler = (error, data) => {
            this._state.loading = false;

			// TODO: this should/could be made more fine-grained (errType is http/timeout/error)
			if (data) {
				this.fire('metadata', data);
			}
			this.fire('error', error);
		};

		const rootElement = this.element();

		if (hasSourceElementChild(rootElement)) {
			metadataFetchFromSource(rootElement, callback);
		} else {
			Config.metadataFetchFunction(this._data.videoId, callback, errorHandler);
		}
	}

    /**
     * Attempts to start the playback of the video.
     *
     * @return {Promise} Returns a Promise that is fulfilled when the video is
     * started or rejected if any error occured.
     */
	play() {
		if (this.state().ready) {
			if (applyFunctionToFilters(this.filters, 'play', arguments)) { // eslint-disable-line prefer-rest-params
				return Promise.reject(new Error('Interrupted by filters.'));
			}

            // The return value of play() varies between browsers. If a
            // promise is returned we pass that as our return value, otherwise
            // we make a guess and resolve a promise directly.
            const result = this.video().play();
            if (typeof result === 'object' && typeof result.then === 'function') {
                return result;
            } else {
                return Promise.resolve();
            }

		} else if (this.state().loading) {
            // The video is not yet ready to be played. We wait for a loadstart
            // (which occurs when the video is being loaded) before we execute
            // play. Note that we create a wrapping promise that resolves with
            // the inner play promise.
            return new Promise((resolve) => {
                const onLoadStart = () => {
                    this.element().removeEventSVP('loadstart', onLoadStart);
                    resolve(this.play());
                };
                this.element().addEventSVP('loadstart', onLoadStart);
            });
		}
	}

	destroy() {
		if (this.video().unload) {
			this.video().unload();
		}
		cleanEvents(this.video());
		_clearResetOnEndListener(this);

		this._data.destroy();
		this._data = undefined;
		this._state = undefined;
		this.filters = undefined;
		this.config = undefined;
		this.device = undefined;

		var noop = function () {
		    // empty
        };
		this.load = noop;
		this.play = noop;
		this.destroy = noop;
		this.on = noop;
		this.off = noop;
		this.fire = noop;
	}

    /**
     * Dispatch an event on the wrapper element
     *
     * @param {string} eventName - The name of the event.
     * @param {object} [data] - The detail data for the CustomEvent.
     * @param {object} [customEventData] - EventInit dictionary to pass to the
     *      CustomEvent. It can specify "bubbles", "cancelable" and "compsed"
     *      fields on the event.
     */
	fire(eventName, data, customEventInit) {
        const el = this.element();
        if (!el) {
            return;
        }

        let detail = data;
        if (eventName === 'error' && data && data.name) {
            detail = Object.assign({
                isExpectedError: isExpectedError(data.name)
            }, data);
        }

        const ev = new SVPCustomEvent(
            eventName,
            Object.assign({ detail }, customEventInit)
        );

        const cancelled = el.dispatchEvent(ev);

        return cancelled;
	}

	on() {
		return this.element() && this.element().addEventSVP && this.element().addEventSVP.apply(this.data()._root, arguments); // eslint-disable-line prefer-rest-params
	}

	off() {
		return this.element() && this.element().removeEventSVP && this.element().removeEventSVP.apply(this.data()._root, arguments); // eslint-disable-line prefer-rest-params
	}

	once(event, callback) {
		var _this = this;
		event = event.match(/\./) ? event : NAMESPACE + '.' + event;

		return this.on(event, function () {
			callback.apply(_this, arguments); // eslint-disable-line prefer-rest-params
			_this.off(event);
		});
	}

	show() {
		domUtils.removeClass(this.element(), STATE_CLASS.forceHide);
		this.state().hidden = false;
	}

	hide() {
		domUtils.addClass(this.element(), STATE_CLASS.forceHide);
		this.state().hidden = true;
	}

	setFullscreenElement(element) {
	    // Fullscreen API in iOS is not supported for other node types than video
	    if (device.isIOS() && element.tagName.toLocaleLowerCase() !== 'video') {
	        return false;
        }

	    this.fullscreenElement = element;

	    return true;
    }

    getFullscreenElement() {
	    return this.fullscreenElement || this.element();
    }

    enterFullscreen() {
	    domUtils.enterVideoFullscreen(this);
    }

    exitFullscreen() {
	    domUtils.exitVideoFullscreen(this);
    }

    setStreams(streams) {
	    this.streams = streams;
    }

    getStreams() {
	    return this.streams;
    }

    isAutoplayOfNextVideo() {
	    return this.autoplayOfNextVideo;
    }
}

function applyFunctionToFiltersOrVideoObject(api, fnName, args) {
    let ret = applyFunctionToFilters(api.filters, fnName, args);
    if (ret === null || ret === undefined) {
        const videoObject = api.video();
        if (videoObject[fnName]) {
            ret = videoObject[fnName].apply(videoObject, args);
        }
    }

    return ret;
}

['pause', 'seek', 'insertButton', 'removeButton', 'showCaptions', 'reset', 'setVolume', 'mute', 'setAudioTrack']
    .forEach((fnName) => {
        WrapperAPI.prototype[fnName] = function () {
            applyFunctionToFiltersOrVideoObject(this, fnName, arguments); // eslint-disable-line prefer-rest-params

            return this;
        };
    });

['getCurrentTime', 'getDuration', 'getVolume', 'getMaxSeekable', 'isMuted', 'getMetrics', 'getSrc', 'isPaused', 'isSeeking', 'getTextTracks', 'getAudioTracks']
    .forEach((fnName) => {
        WrapperAPI.prototype[fnName] = function () {
			return applyFunctionToFiltersOrVideoObject(this, fnName, arguments); // eslint-disable-line prefer-rest-params
		};
	});



// WEBPACK FOOTER //
// ./src/js/modules/wrapperApi.js